import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Grid,
    Tooltip,
    Divider,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Security,
} from '@mui/icons-material';
import { authAPI, companyAPI } from '../../services/api';
import { selectIsSuperAdmin, selectUser } from '../../store/authSlice';
import { canAccessPermission, MODULE_LABELS } from '../../config/permissions';

const Users = () => {
    const currentUser = useSelector(selectUser);
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const canManageUsers = canAccessPermission(currentUser, 'users.manage');
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [permissionCatalog, setPermissionCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rightsLoading, setRightsLoading] = useState(false);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openRightsDialog, setOpenRightsDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedUserForRights, setSelectedUserForRights] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        company_id: '',
        role: 'user'
    });

    useEffect(() => {
        fetchUsers();
        if (isSuperAdmin) {
            fetchCompanies();
        }
        fetchPermissionCatalog();
    }, [isSuperAdmin]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await authAPI.getUsers();
            setUsers(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to load users');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await companyAPI.getAll();
            setCompanies(response.data.data);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const fetchPermissionCatalog = async () => {
        try {
            const response = await authAPI.getPermissionCatalog();
            setPermissionCatalog(response.data.data || []);
        } catch (err) {
            console.error('Failed to load permissions catalog:', err);
        }
    };

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                full_name: user.full_name,
                email: user.email,
                password: '',
                company_id: user.company_id || '',
                role: user.role || user.role_name || 'user'
            });
        } else {
            setEditingUser(null);
            setFormData({
                full_name: '',
                email: '',
                password: '',
                company_id: '',
                role: 'user'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUser(null);
        setFormData({
            full_name: '',
            email: '',
            password: '',
            company_id: '',
            role: 'user'
        });
    };

    const handleSubmit = async () => {
        try {
            const submitData = { ...formData };

            // Don't send password if editing and password is empty
            if (editingUser && !submitData.password) {
                delete submitData.password;
            }

            if (editingUser) {
                await authAPI.updateUser(editingUser.id, submitData);
            } else {
                await authAPI.createUser(submitData);
            }
            handleCloseDialog();
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save user');
        }
    };

    const handleOpenRightsDialog = async (user) => {
        try {
            setRightsLoading(true);
            setSelectedUserForRights(user);
            const response = await authAPI.getUserPermissions(user.id);
            setSelectedPermissions(response.data.data?.permissions || []);
            setOpenRightsDialog(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load user rights');
        } finally {
            setRightsLoading(false);
        }
    };

    const handleCloseRightsDialog = () => {
        setOpenRightsDialog(false);
        setSelectedUserForRights(null);
        setSelectedPermissions([]);
    };

    const handleTogglePermission = (permissionKey) => {
        setSelectedPermissions((prev) =>
            prev.includes(permissionKey)
                ? prev.filter((key) => key !== permissionKey)
                : [...prev, permissionKey]
        );
    };

    const handleSaveRights = async () => {
        if (!selectedUserForRights) return;

        try {
            setRightsLoading(true);
            await authAPI.updateUserPermissions(selectedUserForRights.id, selectedPermissions);
            handleCloseRightsDialog();
            await fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user rights');
        } finally {
            setRightsLoading(false);
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await authAPI.deleteUser(userId);
                fetchUsers();
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete user');
            }
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'super_admin':
                return 'secondary';
            case 'company_admin':
                return 'primary';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={600}>
                    Users Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    disabled={!canManageUsers}
                >
                    Add User
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id} hover>
                                <TableCell>
                                    <Typography fontWeight={500}>{user.full_name}</Typography>
                                </TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    {user.company_name || '-'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={(user.role || 'user').replace('_', ' ').toUpperCase()}
                                        color={getRoleColor(user.role || 'user')}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {new Date(user.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenDialog(user)}
                                        disabled={!canManageUsers}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <Tooltip title="Manage Rights">
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => handleOpenRightsDialog(user)}
                                                disabled={!canManageUsers || user.role === 'super_admin'}
                                            >
                                                <Security />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(user.id)}
                                        disabled={user.role === 'super_admin' || !canManageUsers}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Full Name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        margin="normal"
                        required
                        disabled={!!editingUser}
                    />
                    <TextField
                        fullWidth
                        label={editingUser ? 'Password (leave empty to keep current)' : 'Password'}
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        margin="normal"
                        required={!editingUser}
                    />

                    {isSuperAdmin && !editingUser && (
                        <>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Company</InputLabel>
                                <Select
                                    value={formData.company_id}
                                    label="Company"
                                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                                >
                                    <MenuItem value="">
                                        <em>None (Super Admin)</em>
                                    </MenuItem>
                                    {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.company_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth margin="normal">
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={formData.role}
                                    label="Role"
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <MenuItem value="user">User</MenuItem>
                                    <MenuItem value="company_admin">Company Admin</MenuItem>
                                    <MenuItem value="super_admin">Super Admin</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={!canManageUsers}>
                        {editingUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openRightsDialog} onClose={handleCloseRightsDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Manage Rights: {selectedUserForRights?.full_name}
                </DialogTitle>
                <DialogContent>
                    {rightsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            {Object.entries(
                                permissionCatalog.reduce((groups, perm) => {
                                    const mod = perm.module_name || perm.key?.split('.')[0] || 'other';
                                    if (!groups[mod]) groups[mod] = [];
                                    groups[mod].push(perm);
                                    return groups;
                                }, {})
                            ).map(([module, perms], idx) => (
                                <Box key={module} sx={{ mb: 2 }}>
                                    {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            color: 'text.secondary',
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        {MODULE_LABELS[module] || module}
                                    </Typography>
                                    <Grid container>
                                        {perms.map((permission) => (
                                            <Grid item xs={12} sm={6} key={permission.key}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedPermissions.includes(permission.key)}
                                                            onChange={() => handleTogglePermission(permission.key)}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            {permission.action_name === 'manage' ? 'Manage' : 'View'}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRightsDialog}>Cancel</Button>
                    <Button onClick={handleSaveRights} variant="contained" disabled={rightsLoading || !canManageUsers}>
                        Save Rights
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Users;
